import React, { useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";

export const RolePermissionsModal = ({
  isOpen,
  onClose,
  onSave,
  initialPermissions = [],
  title = "New Role",
  saveButtonText = "Create Role",
  mode = "add", // 'add', 'edit', 'view'
}) => {
  // Access types available in the system
  const accessTypes = [
    { id: "student", name: "Student Access" },
    { id: "supervisor", name: "Supervisor Access" },
    { id: "section_head", name: "Section Head Access" },
    { id: "school_head", name: "School Head Access" },
    { id: "admin", name: "Admin Access" },
    { id: "super_admin", name: "Super Admin Access" },
  ];

  // State to store permissions
  const [permissions, setPermissions] = useState({
    student: { full: false, view: false, edit: false },
    supervisor: { full: false, view: false, edit: false },
    section_head: { full: false, view: false, edit: false },
    school_head: { full: false, view: false, edit: false },
    admin: { full: false, view: false, edit: false },
    super_admin: { full: false, view: false, edit: false },
  });

  // Parse the initialPermissions array to set the initial state
  useEffect(() => {
    if (initialPermissions && initialPermissions.length > 0) {
      const updatedPermissions = { ...permissions };

      initialPermissions.forEach((permission) => {
        if (permission.startsWith("full-access-")) {
          const accessType = permission.replace("full-access-", "");
          if (updatedPermissions[accessType]) {
            updatedPermissions[accessType].full = true;
            updatedPermissions[accessType].view = true;
            updatedPermissions[accessType].edit = true;
          }
        } else if (permission.startsWith("can-view-")) {
          const accessType = permission.replace("can-view-", "");
          if (updatedPermissions[accessType]) {
            updatedPermissions[accessType].view = true;
          }
        } else if (permission.startsWith("can-edit-")) {
          const accessType = permission.replace("can-edit-", "");
          if (updatedPermissions[accessType]) {
            updatedPermissions[accessType].edit = true;
          }
        }
      });

      setPermissions(updatedPermissions);
    }
  }, [initialPermissions]);

  // Handle checkbox changes
  const handlePermissionChange = (accessType, permissionType, checked) => {
    if (mode === "view") return; // Disable changes in view mode

    setPermissions((prev) => {
      const newPermissions = { ...prev };

      // If full access is checked, check view and edit as well
      if (permissionType === "full" && checked) {
        newPermissions[accessType] = { full: true, view: true, edit: true };
      }
      // If full access is unchecked, keep the other permissions as they are
      else if (permissionType === "full" && !checked) {
        newPermissions[accessType] = {
          ...newPermissions[accessType],
          full: false,
        };
      }
      // For view and edit, just toggle their values
      else {
        newPermissions[accessType] = {
          ...newPermissions[accessType],
          [permissionType]: checked,
        };

        // If both view and edit are checked, also check full access
        if (permissionType === "view" || permissionType === "edit") {
          if (
            newPermissions[accessType].view &&
            newPermissions[accessType].edit
          ) {
            newPermissions[accessType].full = true;
          } else {
            newPermissions[accessType].full = false;
          }
        }
      }

      return newPermissions;
    });
  };

  // Convert permissions object back to array of strings for saving
  const getPermissionsArray = () => {
    const result = [];

    Object.entries(permissions).forEach(([accessType, perms]) => {
      if (perms.full) {
        result.push(`full-access-${accessType}`);
      } else {
        if (perms.view) result.push(`can-view-${accessType}`);
        if (perms.edit) result.push(`can-edit-${accessType}`);
      }
    });

    return result;
  };

  // Handle save button click
  const handleSave = () => {
    onSave(getPermissionsArray());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[700px] p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-[#FF9348]">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <IoMdClose size={24} />
          </button>
        </div>

        <h3 className="text-lg font-medium text-center mb-4">
          Assign Permissions
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-2 px-4 text-left w-1/3">Access</th>
                <th className="py-2 px-4 text-center">Full Access</th>
                <th className="py-2 px-4 text-center">View</th>
                <th className="py-2 px-4 text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {accessTypes.map((accessType) => (
                <tr key={accessType.id} className="border-b border-gray-200">
                  <td className="py-4 px-4">{accessType.name}</td>
                  <td className="py-4 px-4 text-center">
                    <div className="relative inline-flex items-center">
                      <input
                        type="checkbox"
                        id={`full-${accessType.id}`}
                        checked={permissions[accessType.id].full}
                        onChange={(e) =>
                          handlePermissionChange(
                            accessType.id,
                            "full",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                        disabled={mode === "view"}
                      />
                      <label
                        htmlFor={`full-${accessType.id}`}
                        className="w-4 h-4 border-2 border-gray-300 rounded bg-white peer-checked:bg-orange-500 peer-checked:border-orange-500 cursor-pointer flex items-center justify-center peer-disabled:opacity-70 peer-disabled:cursor-not-allowed"
                      >
                        {permissions[accessType.id].full && (
                          <svg
                            className="w-3 h-3 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </label>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="relative inline-flex items-center">
                      <input
                        type="checkbox"
                        id={`view-${accessType.id}`}
                        checked={permissions[accessType.id].view}
                        onChange={(e) =>
                          handlePermissionChange(
                            accessType.id,
                            "view",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                        disabled={mode === "view"}
                      />
                      <label
                        htmlFor={`view-${accessType.id}`}
                        className="w-4 h-4 border-2 border-gray-300 rounded bg-white peer-checked:bg-orange-500 peer-checked:border-orange-500 cursor-pointer flex items-center justify-center peer-disabled:opacity-70 peer-disabled:cursor-not-allowed"
                      >
                        {permissions[accessType.id].view && (
                          <svg
                            className="w-3 h-3 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </label>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="relative inline-flex items-center">
                      <input
                        type="checkbox"
                        id={`edit-${accessType.id}`}
                        checked={permissions[accessType.id].edit}
                        onChange={(e) =>
                          handlePermissionChange(
                            accessType.id,
                            "edit",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                        disabled={mode === "view"}
                      />
                      <label
                        htmlFor={`edit-${accessType.id}`}
                        className="w-4 h-4 border-2 border-gray-300 rounded bg-white peer-checked:bg-orange-500 peer-checked:border-orange-500 cursor-pointer flex items-center justify-center peer-disabled:opacity-70 peer-disabled:cursor-not-allowed"
                      >
                        {permissions[accessType.id].edit && (
                          <svg
                            className="w-3 h-3 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={onClose}
            className="px-8 py-2 border border-gray-300 rounded-md"
          >
            Back
          </button>
          {mode !== "view" && (
            <button
              onClick={handleSave}
              className="px-8 py-2 bg-blue-600 text-white rounded-md"
            >
              {saveButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
